<template>
  <div>
    <StakeModal
      v-model="show"
      no-max-width
    >
      <div class="tw-flex tw-flex-col tw-items-center tw-gap-5">
        <h1 class="tw-text-xl">
          Link Meetings
        </h1>

        <div class="tw-flex tw-justify-center tw-gap-7">
          <!-- Meeting Search Results -->
          <div class="meeting-list">
            <input
              v-model="query"
              class="stk-input"
              type="text"
              placeholder="Search for meetings"
              @input="debouncedSearch"
            >

            <ul>
              <MeetingListItem
                v-for="meeting in filteredMeetings"
                :key="meeting.id"
                :meeting="meeting"
                :selected="!!selected[meeting.id]"
                @click="addMeeting(meeting)"
              />
            </ul>
          </div>

          <!-- Selected Meetings -->
          <div class="meeting-list">
            <p class="tw-font-bold tw-text-center">
              {{ selectedArray.length }} Meetings {{ hasChanges ? 'Selected' : 'Linked' }}
            </p>

            <ul>
              <MeetingListItem
                v-for="meeting in selectedArray"
                :key="meeting.id"
                :meeting="meeting"
                :linked="!!linked[meeting.id]"
                icon="fa-unlink"
                @click="removeMeeting(meeting)"
              />
            </ul>
          </div>
        </div>

        <div class="tw-flex tw-items-start tw-gap-5">
          <Button
            variant="primary tw-w-48"
            :disabled="!hasChanges || !loaded"
            :icon-before="`fas fa-${hasChanges ? 'save' : 'check'}`"
            :loading="saving"
            @click="updateEntityMeetings"
          >
            {{ hasChanges ? 'Save Changes' : 'Saved' }}
          </Button>

          <Button
            class=""
            @click="cancel"
          >
            Cancel
          </Button>
        </div>
      </div>
    </StakeModal>
  </div>
</template>


<script setup>
import {
  defineProps, defineEmits,
  ref, set, del,
  computed, watch,
  onMounted,
  getCurrentInstance,
} from 'vue';

// Allows us to use $oruga
const { proxy } = getCurrentInstance();

import { debounce } from 'lodash';

import { StakeModal, Button } from '@stakeui';
import MeetingListItem from '@features/dossier/components/MeetingListItem.vue';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { useDossierStore } from '@frontend/ui/stores/dossierStore.js';
const advisorStore = useAdvisorStore();
const dossierStore = useDossierStore();


const props = defineProps({
  value: Boolean,
  entityExternalId: {
    type: String,
    required: true,
  },
});


// Reactive state
// -------------------------------------------------------------------------------->
/* eslint-disable */
const show     = ref(props.value);
const loaded   = ref(false);
const meetings = ref([]);
const selected = ref({});
const linked   = ref({});
const query    = ref('');
const saving   = ref(false);
/* eslint-enable */

// For display purposes
const selectedArray = computed(() => Object.values(selected.value));


// Computed props
// -------------------------------------------------------------------------------->

const filteredMeetings = computed(() => {
  if (!query.value) return meetings.value;

  return meetings.value.filter(meeting => {
    if (!meeting.title) return false;
    const title = meeting.title.toLowerCase();
    return title.includes(query.value.toLowerCase());
  });
});

const toAdd = computed(() => {
  return selectedArray.value.filter(meeting => !linked.value[meeting.id]).length;
});

const toRemove = computed(() => {
  return Object.keys(linked.value).filter(id => !selected.value[id]).length;
});

const hasChanges = computed(() => {
  return toAdd.value + toRemove.value > 0;
});


// Events
// -------------------------------------------------------------------------------->
const emit = defineEmits(['input']);


// Watchers
// -------------------------------------------------------------------------------->

// Notify parent when modal is closed
watch(show, newValue => {
  if (!newValue) emit('input', false);
});

// Keep local modal state in sync with parent
watch(() => props.value, newValue => {
  show.value = newValue;
});

// Refresh entity meetings when requested
watch(() => dossierStore.lastRefresh, () => {
  getEntityMeetings();
});


// Manage meeting search results
// -------------------------------------------------------------------------------->

const getMeetings = async title => {
  const errorMessage = 'Unable to load meetings. Please try again or contact support.';

  try {
    const params = {
      include_upcoming: false,
      limit: 50,
    };
    if (title) params.title = title;
    const query = new URLSearchParams(params).toString();

    const res = await fetch(`/api/v1/meetings/?${query}`, {
      method: 'GET',
      headers: advisorStore.headers,
    });

    if (!res.ok) {
      danger(errorMessage);
      return [];
    }

    const data = await res.json();
    return data.meetings;
    //
  } catch (error) {
    console.error(error);
    danger(errorMessage);
    // TODO: Sentry
    return [];
  }
};


const getDefaultMeetings = async () => {
  meetings.value = await getMeetings();
};


const searchMeetings = async event => {
  meetings.value = await getMeetings(event.target.value);
};

const debouncedSearch = debounce(searchMeetings, 300);


// Manage selected meetings
// -------------------------------------------------------------------------------->

const getEntityMeetings = async () => {
  const errorMessage = 'Unable to load linked meetings. Please try again or contact support.';
  loaded.value = false;

  try {
    const res = await fetch(`/api/v1/entities/${props.entityExternalId}/meetings/`, {
      method: 'GET',
      headers: advisorStore.headers,
    });

    if (!res.ok) {
      danger(errorMessage);
      return;
    }

    const data = await res.json();
    data.meetings.forEach(meeting => addMeeting(meeting));

    // Convert arrary to object for easy lookup
    linked.value = data.meetings.reduce((acc, meeting) => {
      acc[meeting.id] = meeting;
      return acc;
    }, {});

    dossierStore.linkedMeetingCount = data.meetings.length;
    loaded.value = true;
    //
  } catch (error) {
    danger(errorMessage);
    throw error;
  }
};


const updateEntityMeetings = async () => {
  const errorMessage = 'Unable to save changes. Please try again or contact support.';
  saving.value = true;

  try {
    const ids = selectedArray.value.map(meeting => meeting.id);

    const res = await fetch(`/api/v1/entities/${props.entityExternalId}/meetings/`, {
      method: 'POST',
      headers: advisorStore.headers,
      body: JSON.stringify({ meeting_ids: ids }),
    });

    if (!res.ok) {
      danger(errorMessage);
      saving.value = false;
      return;
    }

    const data = await res.json();

    // Convert arrary to object for easy lookup
    linked.value = data.meetings.reduce((acc, meeting) => {
      acc[meeting.id] = meeting;
      return acc;
    }, {});

    saving.value = false;
    dossierStore.linkedMeetingCount = data.meetings.length;
    setTimeout(() => show.value = false, 500);
    //
  } catch (error) {
    danger(errorMessage);
    saving.value = false;
    throw error;
  }
};


const addMeeting = meeting => {
  set(selected.value, meeting.id, meeting);
};

const removeMeeting = meeting => {
  del(selected.value, meeting.id);
};

const cancel = () => {
  selected.value = { ...linked.value };
  show.value = false;
};


// Other methods
// -------------------------------------------------------------------------------->


const danger = message => {
  proxy.$oruga.notification.open({
    message,
    variant: 'danger',
    position: 'top',
    duration: 5000,
  });
};


// Lifecycle hooks
// -------------------------------------------------------------------------------->
onMounted(() => {
  // getEntity();
  getDefaultMeetings();
  getEntityMeetings();
});
</script>


<style scoped>
.meeting-list {
  @apply tw-flex tw-flex-col tw-justify-end tw-gap-2 tw-w-96;
}

.meeting-list ul {
  @apply tw-flex tw-flex-col tw-gap-2;
  @apply tw-overflow-y-auto tw-h-80 tw-p-3;
  @apply tw-shadow-md tw-rounded-lg tw-p-3 tw-bg-slate-50;
}
</style>
